import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
export const insightsSchema = z.object({
    /** Every day the user has been active, regardless of the range. */
    totalActiveDays: z.number(),
    activeDaysInRange: z.number(),
    /** Days in the range with at least one journal created. */
    journalingDays: z.number(),
    journalEntries: z.number(),
    tasksCompleted: z.number(),
    /** Tasks starting, due or completed in the range - never fewer than `tasksCompleted`. */
    tasksTotal: z.number(),
    currentStreak: z.number(),
    longestStreak: z.number(),
});
export const getInsightsQueryKey = ({ startDate, endDate }) => ['insights', startDate, endDate];
/**
 * The Insights page's key statistics for a range of the user's local days. Journals are
 * placed on days in the browser's time zone, the same one the range was worked out in.
 */
export const useGetInsights = (dateRange, authenticatedRequest) => {
    return useQuery({
        queryKey: getInsightsQueryKey(dateRange ?? { startDate: '', endDate: '' }),
        queryFn: () => authenticatedRequest({
            url: '/insights',
            method: 'GET',
            params: {
                ...dateRange,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
        }, insightsSchema),
        enabled: !!dateRange && dateRange.startDate <= dateRange.endDate,
    });
};
export const dailyCompletionsSchema = z.object({
    /** Oldest first. Days with nothing completed are left out. */
    days: z.array(z.object({
        date: z.string(),
        tasks: z.number(),
        /** Habits completed, each counted once however many times it was done that day. */
        habits: z.number(),
    })),
});
export const getDailyCompletionsQueryKey = ({ startDate, endDate }) => ['insights', 'completions', startDate, endDate];
/** The tasks and habits completed on each of the user's local days in a range. */
export const useGetDailyCompletions = (dateRange, authenticatedRequest) => {
    return useQuery({
        queryKey: getDailyCompletionsQueryKey(dateRange ?? { startDate: '', endDate: '' }),
        queryFn: () => authenticatedRequest({ url: '/insights/completions', method: 'GET', params: { ...dateRange } }, dailyCompletionsSchema),
        enabled: !!dateRange && dateRange.startDate <= dateRange.endDate,
    });
};
export const dailyProductivitySchema = z.object({
    /** Oldest first. Days with nothing due are left out, as are days after today. */
    days: z.array(z.object({
        date: z.string(),
        /** Tasks placed on the day plus habit-days due on it. */
        due: z.number(),
        done: z.number(),
        /** The due items linked to a goal or in a life domain rated 2 or lower for satisfaction. */
        intentionalDue: z.number(),
        intentionalDone: z.number(),
    })),
});
export const getDailyProductivityQueryKey = ({ startDate, endDate }) => ['insights', 'productivity', startDate, endDate];
/**
 * How much of what was due on each of the user's local days in a range got done, overall and
 * for intentional items. "Today" is the browser's, so it is sent with its time zone.
 */
export const useGetDailyProductivity = (dateRange, authenticatedRequest) => {
    return useQuery({
        queryKey: getDailyProductivityQueryKey(dateRange ?? { startDate: '', endDate: '' }),
        queryFn: () => authenticatedRequest({
            url: '/insights/productivity',
            method: 'GET',
            params: {
                ...dateRange,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
        }, dailyProductivitySchema),
        enabled: !!dateRange && dateRange.startDate <= dateRange.endDate,
    });
};
